import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Folder,
  Plus,
  Share2,
  Pencil,
  Trash2,
  ClipboardList,
  FileText,
  ChevronRight,
  X,
  Users as UsersIcon,
  User as UserIcon,
} from 'lucide-react';
import { formatRelativeTime } from 'lib/relativeTime';
import * as CollectionsModule from 'modules/collections';
import * as AuthModule from 'modules/auth';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { toPlainText } from 'modules/editor';
import { CollectionFormModal } from 'pages/Library/CollectionFormModal';
import { ShareCollectionModal } from 'pages/Collections/ShareCollectionModal';
import { AddItemsModal } from 'pages/Collections/AddItemsModal';
import {
  MobileScreen,
  ScreenHeader,
  ScreenBody,
  RowGroup,
  Row,
  MobileSkeleton,
  MobileEmpty,
  MobileError,
  M,
} from 'components/mobile';

/** Reuses the exact same hooks + modals as the desktop page. */
export function CollectionDetailMobile() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    state: { user },
  } = AuthModule.Context.useAuthContext();
  const canOpenItem = usePermission(PERMISSIONS.TESTS_EDIT);

  const { collection, isLoading, isError, error, refetch } = CollectionsModule.Hooks.useSingle({
    id: id || '',
    enabled: Boolean(id),
  });
  const removeItem = CollectionsModule.Hooks.useRemoveItem();
  const deleteCollection = CollectionsModule.Hooks.useDelete();

  const [editOpen, setEditOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const backToLibrary = () => navigate('/app/library');

  if (isLoading) {
    return (
      <MobileScreen>
        <ScreenHeader title={t('collections.collection', 'Collection')} onBack={backToLibrary} />
        <ScreenBody>
          <MobileSkeleton rows={4} />
        </ScreenBody>
      </MobileScreen>
    );
  }

  if (isError) {
    return (
      <MobileScreen>
        <ScreenHeader title={t('collections.collection', 'Collection')} onBack={backToLibrary} />
        <ScreenBody>
          <MobileError
            description={error instanceof Error ? error.message : undefined}
            onRetry={() => refetch()}
          />
        </ScreenBody>
      </MobileScreen>
    );
  }

  if (!collection) {
    return (
      <MobileScreen>
        <ScreenHeader title={t('collections.collection', 'Collection')} onBack={backToLibrary} />
        <ScreenBody>
          <MobileEmpty
            icon={<Folder size={22} />}
            title={t('collections.not_found')}
            action={
              <button
                type="button"
                onClick={backToLibrary}
                className="rounded-md px-4 py-2 text-[12.5px] font-bold text-white"
                style={{ background: M.accent }}
              >
                {t('collections.back_to_library')}
              </button>
            }
          />
        </ScreenBody>
      </MobileScreen>
    );
  }

  const items = collection.items ?? [];
  const sharedWith = collection.sharedWith ?? [];
  const existingTestIds = items.filter((i) => i.type === 'test').map((i) => i.itemId);
  const isOwner = Boolean(user?.id) && collection.ownerId === user?.id;
  const canEdit = isOwner || collection.canEdit === true;
  const updatedLabel = formatRelativeTime(collection.updatedAt, i18n.language);

  const handleRemoveItem = (type: 'test' | 'question', itemId: string) => {
    if (!id) return;
    removeItem.mutate({ collectionId: id, type, itemId });
  };
  const handleDelete = () => {
    if (!id) return;
    if (window.confirm(t('collections.delete_confirm'))) {
      deleteCollection.mutate(id, { onSuccess: backToLibrary });
    }
  };

  const metaParts = [t('collections.items_count', { count: items.length })];
  if (updatedLabel) metaParts.push(t('collections.updated_ago', { when: updatedLabel }));

  return (
    <MobileScreen>
      <ScreenHeader
        title={collection.name}
        onBack={backToLibrary}
        right={
          canEdit ? (
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              aria-label={t('collections.add_items')}
              className="-m-[7px] flex h-11 w-11 items-center justify-center p-[7px]"
            >
              <span
                className="flex h-[30px] w-[30px] items-center justify-center rounded-md text-white"
                style={{ background: M.accent }}
              >
                <Plus size={17} strokeWidth={2.5} />
              </span>
            </button>
          ) : undefined
        }
      />

      <ScreenBody>
        {/* Identity hero — solid ink, no gradient or decorative blob. */}
        <div
          className="relative mb-3.5 flex items-center gap-4 overflow-hidden rounded-lg p-5 text-white"
          style={{ background: M.accent }}
        >
          <span
            className="relative flex h-[52px] w-[52px] flex-shrink-0 items-center justify-center rounded-lg"
            style={{ background: 'rgba(255,255,255,.16)' }}
          >
            <Folder size={26} />
          </span>
          <div className="relative min-w-0">
            <div className="truncate text-[19px] font-extrabold tracking-[-0.02em]">{collection.name}</div>
            {collection.description && (
              <div className="mt-0.5 line-clamp-1 text-[12.5px]" style={{ color: 'rgba(255,255,255,.8)' }}>
                {collection.description}
              </div>
            )}
            <div className="mt-1 truncate text-[12px]" style={{ color: 'rgba(255,255,255,.78)' }}>
              {metaParts.join(' · ')}
            </div>
          </div>
        </div>

        {/* Owner/editor actions */}
        {(canEdit || isOwner) && (
          <div className="mb-3.5 flex flex-wrap gap-2">
            {isOwner && (
              <ActionChip icon={<Share2 size={15} />} label={t('collections.share')} onClick={() => setShareOpen(true)} />
            )}
            {canEdit && (
              <ActionChip icon={<Pencil size={15} />} label={t('collections.edit_collection', 'Edit')} onClick={() => setEditOpen(true)} />
            )}
            {isOwner && (
              <ActionChip
                icon={<Trash2 size={15} />}
                label={t('delete', 'Delete')}
                onClick={handleDelete}
                danger
              />
            )}
          </div>
        )}

        {/* Shared-with chips */}
        {sharedWith.length > 0 && (
          <div className="mb-3.5 flex flex-wrap items-center gap-1.5">
            {sharedWith.map((s) => (
              <span
                key={`${s.type}-${s.targetId}`}
                className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-semibold"
                style={{ background: M.muted, color: M.ink2 }}
              >
                {s.type === 'team' ? <UsersIcon size={12} /> : <UserIcon size={12} />}
                {s.target?.name ?? (s.type === 'team' ? t('collections.team') : t('collections.user'))}
                <span style={{ color: M.faint }}>· {s.access === 'edit' ? t('collections.can_edit') : t('collections.can_view')}</span>
              </span>
            ))}
          </div>
        )}

        {/* Items */}
        {items.length === 0 ? (
          <MobileEmpty
            icon={<FileText size={22} />}
            title={t('collections.no_items_title')}
            description={t('collections.no_items_desc')}
            action={
              canEdit ? (
                <button
                  type="button"
                  onClick={() => setAddOpen(true)}
                  className="rounded-md px-4 py-2 text-[12.5px] font-bold text-white"
                  style={{ background: M.accent }}
                >
                  {t('collections.add_items')}
                </button>
              ) : undefined
            }
          />
        ) : (
          <RowGroup>
            {items.map((item, i) => {
              const isTest = item.type === 'test';
              const clickable = canOpenItem && isTest;
              const title =
                item.item?.title || toPlainText(item.item?.prompt) || t('collections.untitled');
              return (
                <Row
                  key={`${item.type}-${item.itemId}`}
                  last={i === items.length - 1}
                  onClick={clickable ? () => navigate(`/app/tests/${item.itemId}`) : undefined}
                >
                  <span
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md"
                    style={{
                      background: isTest ? M.accentTint : M.muted,
                      color: isTest ? M.accent : M.ink2,
                    }}
                  >
                    {isTest ? <ClipboardList size={17} /> : <FileText size={17} />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13.5px] font-semibold" style={{ color: M.ink }}>
                      {title}
                    </div>
                    <div className="text-[11.5px] capitalize" style={{ color: M.faint }}>
                      {item.type}
                    </div>
                  </div>
                  {canEdit ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveItem(item.type, item.itemId);
                      }}
                      aria-label={t('collections.remove_from_collection', 'Remove from collection')}
                      className="-m-2 flex h-9 w-9 items-center justify-center p-2"
                      style={{ color: M.faint }}
                    >
                      <X size={16} />
                    </button>
                  ) : (
                    clickable && <ChevronRight size={17} style={{ color: M.chevron }} />
                  )}
                </Row>
              );
            })}
          </RowGroup>
        )}
      </ScreenBody>

      <CollectionFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        collection={{ id: collection.id, name: collection.name, description: collection.description }}
        onSuccess={() => setEditOpen(false)}
      />
      <ShareCollectionModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        collectionId={collection.id}
        currentShares={sharedWith}
        onChanged={() => undefined}
      />
      <AddItemsModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        collectionId={collection.id}
        existingTestIds={existingTestIds}
        onAdded={() => undefined}
      />
    </MobileScreen>
  );
}

function ActionChip({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-9 items-center gap-1.5 rounded-md px-3 text-[12.5px] font-semibold"
      style={{
        background: M.card,
        border: `1px solid ${M.border}`,
        color: danger ? M.redBright : M.ink2,
      }}
    >
      {icon}
      {label}
    </button>
  );
}

export default CollectionDetailMobile;
